import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { type IFrameExitArgs } from "@client/lib/misc";
import { type BaseAppState, type EntityRef, Status } from "@client/types";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import { setActiveCourse, setActiveLessonId } from "../CourseDetail/actions";
import { getUrlState, mapToCourseAppState, pushUrlState } from "../CourseDetail/lib";
import api from "./api";
import { localStateSelector } from "./selectors";
import { type CourseList } from "./types";

export enum Actions {
  InitBegin = "MEMBER_COURSE_LIST_INIT_BEGIN",
  InitFailure = "MEMBER_COURSE_LIST_INIT_FAILURE",
  InitSuccess = "MEMBER_COURSE_LIST_INIT_SUCCESS",

  SetSearchText = "MEMBER_COURSE_LIST_SET_SEARCH_TEXT"
}

export type InitBeginActionData = EntityRef & {
  courseId?: string;
  isReload: boolean;
};
export type InitSuccessActionData = {
  activeCourse: CourseList.CourseAppState | null;
  activeLesson: CourseList.ClientLesson | null;
  courses: CourseList.Course[] | null;
};
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  CourseList.Api.GetCoursesResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: [
      Actions.InitSuccess,
      ({ dispatch, getState, params, res }) => {
        const localState = localStateSelector(getState(), params);
        const urlState = getUrlState();
        if (urlState.csid && urlState.cscid) {
          if (localState.activeCourse) {
            const lesson = localState.activeCourse.lessons.find((x) => x.id === urlState.cscid);
            if (lesson && lesson.moduleId === urlState.csid && !lesson.isLocked) {
              dispatch(setActiveLessonId({ id: params.id, source: params.source, lessonId: urlState.cscid }));
              return;
            }
          }
        } else if (params.courseId && localState.availableCourses.find((x) => x.id === params.courseId)?.isEnrolled) {
          dispatch(setActiveCourse({ id: params.id, source: params.source }, params.courseId));
        }

        pushUrlState({
          ...urlState,
          cid: undefined,
          csid: undefined,
          cscid: undefined
        });
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.getCourses(apiInfo, { portalId: state.portal.id, id: params.courseId });
  },
  mapResponse: (state: BaseAppState, resp: CourseList.Api.GetCoursesResponse): InitSuccessActionData => ({
    activeCourse: mapToCourseAppState(resp.activeCourse, !!state.portal.programId),
    activeLesson: resp.activeCourse?.activeLesson ?? null,
    courses: resp.courses
  })
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    const urlState = getUrlState();
    dispatch(_init({ ...args, courseId: urlState.cid, isReload: false }));
  }
};

export const reload =
  (ref: EntityRef, exitArgs: IFrameExitArgs | undefined) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const urlState = getUrlState();

    dispatch(
      _init({
        ...ref,
        courseId: exitArgs?.reason === "membership_disabled" ? exitArgs?.programId : urlState.cid,
        isReload: true
      })
    );
  };

export type SetSearchTextActionData = EntityRef & {
  searchText: string | null;
};
export const setSearchText = (ref: EntityRef, searchText: string | null) => ({
  type: Actions.SetSearchText,
  data: <SetSearchTextActionData>{
    ...ref,
    searchText
  }
});
